/**
 * Service to fetch and extract metadata from websites
 */

/**
 * Fetches metadata from a URL using a proxy service to avoid CORS issues
 * @param {string} url - The URL to fetch metadata from
 * @returns {Promise<Object>} - Object containing title, description, etc.
 */
export const fetchMetadata = async (url) => {
  if (!url || !url.trim().startsWith('http')) {
    return { success: false, error: 'Invalid URL' };
  }

  try {
    // Since we can't reliably fetch external sites due to CORS,
    // we'll generate metadata based on the URL domain
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      const suggestedName = domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
      
      return {
        success: true,
        metadata: {
          title: suggestedName,
          description: `Website at ${domain}`,
          siteName: domain,
          suggestedTags: generateTagSuggestions(url)
        }
      };
    } catch (e) {
      return { success: false, error: 'Could not extract metadata' };
    }
  } catch (error) {
    console.error('Error fetching metadata:', error);
    return { success: false, error: 'Failed to fetch metadata' };
  }
};

/**
 * Generates tag suggestions based on URL
 */
function generateTagSuggestions(url) {
  const tags = [];
  
  try {
    const domain = new URL(url).hostname.toLowerCase();
    const domainTag = domain.split('.')[0].toLowerCase();
    tags.push(domainTag);
    
    // Add TLD-based tags
    const tld = domain.split('.').pop();
    if (tld === 'dev' || tld === 'io') tags.push('development');
    if (tld === 'org') tags.push('organization');
    
    // AI-specific suggestions based on common domains
    if (domain.includes('openai') || domain.includes('chatgpt')) {
      tags.push('ai', 'chatbot');
    } else if (domain.includes('claude') || domain.includes('anthropic')) {
      tags.push('ai', 'chatbot');
    } else if (domain.includes('midjourney') || domain.includes('dalle') || domain.includes('leonardo')) {
      tags.push('ai', 'images', 'creative');
    } else if (domain.includes('runway') || domain.includes('pika')) {
      tags.push('ai', 'videos', 'creative');
    } else if (domain.includes('eleven') || domain.includes('murf') || domain.includes('voice')) {
      tags.push('ai', 'voice');
    } else if (domain.includes('suno') || domain.includes('udio') || domain.includes('music')) {
      tags.push('ai', 'music');
    } else if (domain.includes('github') && url.includes('copilot')) {
      tags.push('ai', 'coding', 'development');
    } else if (domain.includes('huggingface')) {
      tags.push('ai', 'development');
    }
  } catch (e) {
    // Ignore URL parsing errors
  }
  
  // Remove duplicates and limit number of tags
  return [...new Set(tags)].slice(0, 5);
}